// Presentation helpers over the generated plan catalog.
//
// Plan names, prices, limits, feature inclusion, and comparison rows come only
// from ./generated/planCatalog, which is generated from the authoritative
// catalog in FA-Licensing-Bridge (catalog/versions) by
// `python scripts/catalog.py sync`. Do not add plan facts to this file or to
// page copy: change the catalog, run sync, and rebuild. `npm run catalog:check`
// fails when the generated files are stale or edited by hand, or when page
// sources hardcode plan prices or retired plan names.
//
// This data is informational. FileAgent and the licensing bridge enforce
// entitlements from signed licenses; checkout prices are resolved server-side.
import {
  planCatalog,
  type BillingInterval,
  type CatalogCell,
  type CatalogDefinition,
  type CatalogGroup,
  type CatalogPlan,
  type CatalogRow,
  type PlanId,
} from "./generated/planCatalog";

export type { BillingInterval, CatalogCell, CatalogDefinition, CatalogGroup, CatalogRow, PlanId };
export type Plan = CatalogPlan;
/** Plan identifier used in checkout URLs and public config (e.g. "pro"). */
export type PlanCode = PlanId;

export const plans: Plan[] = [...planCatalog.plans].sort((a, b) => a.order - b.order);
export const planOrder: PlanId[] = plans.map((plan) => plan.id);
export const comparisonGroups: CatalogGroup[] = planCatalog.matrix;
export const planDefinitions: CatalogDefinition[] = planCatalog.definitions;
/** Oldest FileAgent release that can enforce the plans offered today. */
export const minimumAppVersion: string | undefined = planCatalog.minimumAppVersion;

export function planById(id: PlanId): Plan {
  const plan = plans.find((item) => item.id === id);
  if (!plan) throw new Error(`Unknown plan: ${id}`);
  return plan;
}

export function definition(id: string): CatalogDefinition {
  const item = planDefinitions.find((entry) => entry.id === id);
  if (!item) throw new Error(`Unknown catalog definition: ${id}`);
  return item;
}

/** Anchor id used for plan cards; FileAgent upgrade links deep-link to it. */
export function planAnchor(plan: Plan): string {
  return `plan-${plan.id}`;
}

export const recommendedPlan: Plan | undefined = plans.find((plan) => plan.recommendation);
export const freePlan: Plan | undefined = plans.find((plan) => plan.purchase === "download");
export const selfServePlans: Plan[] = plans.filter((plan) => plan.purchase === "self_serve");
export const contactSalesPlan: Plan | undefined = plans.find((plan) => plan.purchase === "contact_sales");

// ---------------------------------------------------------------------------
// Feature availability
// ---------------------------------------------------------------------------

export function featureLabel(featureId: string): string {
  const feature = planCatalog.features.find((item) => item.id === featureId);
  if (!feature) throw new Error(`Unknown feature: ${featureId}`);
  return feature.label;
}

/** Lower-cases only the first character, for labels used mid-sentence. */
export function midSentence(text: string): string {
  return text ? text.charAt(0).toLowerCase() + text.slice(1) : text;
}

export function plansWithFeature(featureId: string): Plan[] {
  const included = plans.filter((plan) => plan.features.includes(featureId));
  if (!included.length) throw new Error(`No current plan includes ${featureId}`);
  return included;
}

export function minimumPlanFor(featureId: string): Plan {
  return plansWithFeature(featureId)[0];
}

/** "Business and Enterprise" / "Pro, Business, and Enterprise". */
export function planNameList(list: Plan[]): string {
  const names = list.map((plan) => plan.name);
  if (names.length <= 1) return names.join("");
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

/** Plans that include a feature, e.g. "Business and Enterprise". */
export function availabilityText(featureId: string): string {
  const included = plansWithFeature(featureId);
  return included.length === plans.length ? "every plan" : planNameList(included);
}

/** Least plan that includes a feature, as "<plan> and higher" or "every plan". */
export function availabilityFrom(featureId: string): string {
  const included = plansWithFeature(featureId);
  if (included.length === plans.length) return "every plan";
  if (included.length === 1) return included[0].name;
  return `${included[0].name} and higher`;
}

/** Compact tag for feature cards: "<plan>+", a single plan name, or "All plans". */
export function minimumPlanTag(featureId: string): string {
  const included = plansWithFeature(featureId);
  if (included.length === plans.length) return "All plans";
  return included.length === 1 ? included[0].name : `${included[0].name}+`;
}

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------

/** Numeric limit values across plans that publish a count (ignores Unlimited/Custom). */
export function countLimitValues(limitId: string): number[] {
  return plans
    .map((plan) => plan.limits[limitId])
    .filter((limit) => limit?.kind === "count" && typeof limit.value === "number")
    .map((limit) => limit.value as number);
}

/** A limit as prose, e.g. "1 user", "unlimited credentials", "custom active jobs". */
export function limitPhrase(plan: Plan, limitId: string): string {
  const limit = plan.limits[limitId];
  const meta = planCatalog.limits.find((item) => item.id === limitId);
  if (!limit || !meta) throw new Error(`Plan ${plan.id} has no ${limitId} limit`);
  if (limit.kind === "count" && typeof limit.value === "number") {
    return `${limit.value.toLocaleString("en-US")} ${limit.value === 1 ? meta.unitSingular : meta.unitPlural}`;
  }
  if (limit.kind === "unlimited") return `unlimited ${meta.unitPlural}`;
  return `${midSentence(limit.label ?? "Custom")} ${meta.unitPlural}`;
}

export function limitDisplay(plan: Plan, limitId: string): string {
  const limit = plan.limits[limitId];
  if (!limit) throw new Error(`Plan ${plan.id} has no ${limitId} limit`);
  return limit.display;
}

// ---------------------------------------------------------------------------
// Prices (integer USD minor units from the catalog)
// ---------------------------------------------------------------------------

export function formatUsdMinor(amountMinor: number): string {
  const dollars = amountMinor / 100;
  const hasCents = amountMinor % 100 !== 0;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  });
}

function fixedAmount(plan: Plan, interval: BillingInterval): number | null {
  const price = plan.prices[interval];
  return price.kind === "fixed" && typeof price.amountMinor === "number" ? price.amountMinor : null;
}

export function isCustomPrice(plan: Plan): boolean {
  return fixedAmount(plan, "monthly") === null || fixedAmount(plan, "annual") === null;
}

export function isFreePrice(plan: Plan): boolean {
  return fixedAmount(plan, "monthly") === 0 && fixedAmount(plan, "annual") === 0;
}

/** Headline amount on a plan card. */
export function priceAmount(plan: Plan, interval: BillingInterval): string {
  const amount = fixedAmount(plan, interval);
  return amount === null ? "Custom" : formatUsdMinor(amount);
}

/** Unit next to the headline amount. Annual prices are yearly totals. */
export function priceUnit(plan: Plan, interval: BillingInterval): string {
  const amount = fixedAmount(plan, interval);
  if (!amount) return "";
  return interval === "annual" ? "/year" : "/month";
}

/** Monthly equivalent of an annual total, for comparison only (rounded to the cent). */
export function annualMonthlyEquivalentMinor(plan: Plan): number | null {
  const annual = fixedAmount(plan, "annual");
  return annual ? Math.round(annual / 12) : null;
}

/** Line under the price that states how the plan is billed. */
export function billingNote(plan: Plan, interval: BillingInterval): string {
  if (isCustomPrice(plan)) {
    return interval === "annual" ? plan.prices.annual.display : "Quoted and invoiced by agreement";
  }
  if (isFreePrice(plan)) return "No credit card required";
  if (interval === "monthly") return "Billed monthly";
  const equivalent = annualMonthlyEquivalentMinor(plan);
  return equivalent ? `Billed annually · ${formatUsdMinor(equivalent)}/mo equivalent` : "Billed annually";
}

/** Yearly savings of the annual subscription versus twelve monthly charges. */
export function annualSavingsNote(plan: Plan): string | null {
  const saved = plan.billing.annualSavingsMinor;
  return saved ? `Save ${formatUsdMinor(saved)} per year vs. monthly billing` : null;
}

/** Label for the annual toggle when every self-serve plan saves the same number of months. */
export function annualToggleNote(): string | null {
  const months = new Set(selfServePlans.map((plan) => plan.billing.annualSavingsMonths ?? 0));
  if (months.size !== 1) return null;
  const [value] = Array.from(months);
  if (!value) return null;
  return value === 1 ? "1 month free" : `${value} months free`;
}

/** Compact price for the comparison table header. */
export function compactPrice(plan: Plan, interval: BillingInterval): string {
  const amount = fixedAmount(plan, interval);
  if (amount === null) return plan.prices[interval].display;
  if (amount === 0) return formatUsdMinor(0);
  return `${formatUsdMinor(amount)}${interval === "annual" ? "/yr" : "/mo"}`;
}
